import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  Upload,
  Database,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@/components/ui";
import { PageHeader } from "@/components/common";
import { usePermissions } from "@/stores";
import {
  ENTITY_META,
  SUPPORTED_ENTITIES,
  fetchExport,
  useImportData,
  type DataBundle,
  type EntityName,
  type ImportResult,
} from "../api";

const GROUPS = {
  catalog: { title: "Catalog", description: "Menu, pricing, recipes" },
  ops: { title: "Operations", description: "Inventory, promotions" },
  people: { title: "People", description: "Customers and users" },
} as const;

export function BackupPage() {
  const { hasPermission } = usePermissions();
  const canExport = hasPermission("data.export");
  const canImport = hasPermission("data.import");

  const [selected, setSelected] = useState<Set<EntityName>>(
    () => new Set(SUPPORTED_ENTITIES),
  );
  const [isExporting, setIsExporting] = useState(false);
  const [pendingBundle, setPendingBundle] = useState<DataBundle | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const importData = useImportData();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, EntityName[]>();
    for (const e of SUPPORTED_ENTITIES) {
      const g = ENTITY_META[e].group;
      const list = map.get(g) ?? [];
      list.push(e);
      map.set(g, list);
    }

    return map;
  }, []);

  const toggle = (entity: EntityName) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);

      return next;
    });

  const handleExport = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one entity to export");

      return;
    }
    setIsExporting(true);
    try {
      const list = SUPPORTED_ENTITIES.filter((e) => selected.has(e));
      const bundle = await fetchExport(
        list.length === SUPPORTED_ENTITIES.length ? null : list,
      );
      const filename = `coffee-pos-backup-${bundle.exportedAt.replace(
        /[:.]/g,
        "-",
      )}.json`;
      const blob = new Blob([JSON.stringify(bundle, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${list.length} entity types`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilePick = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as DataBundle;
      if (parsed.version !== "1") {
        toast.error("Unrecognized bundle version (expected 1)");

        return;
      }
      setPendingBundle(parsed);
    } catch {
      toast.error("Could not parse the selected file as a backup bundle");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingBundle) return;
    try {
      const result = await importData.mutateAsync(pendingBundle);
      setImportResult(result);
      setPendingBundle(null);
      toast.success(
        `Import finished: ${result.totalRows} rows across ${result.summaries.length} entities`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Backup & Restore"
        description="Export the catalog, customers, users and operations data — and restore from a previous export."
      />
      {!canExport && !canImport && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            You don't have backup permissions.
          </CardContent>
        </Card>
      )}

      {(canExport || canImport) && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Export
              </CardTitle>
              <CardDescription>
                Download selected entities as a JSON bundle. Passwords and PINs
                are never included.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[...grouped.entries()].map(([group, entityList]) => {
                const groupMeta = GROUPS[group as keyof typeof GROUPS];
                const allOn = entityList.every((e) => selected.has(e));

                return (
                  <div key={group}>
                    <div className="mb-2 flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold">
                          {groupMeta.title}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {groupMeta.description}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (allOn)
                              entityList.forEach((e) => next.delete(e));
                            else entityList.forEach((e) => next.add(e));

                            return next;
                          })
                        }
                      >
                        {allOn ? "Clear group" : "Select group"}
                      </button>
                    </div>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      {entityList.map((entity) => (
                        <label
                          key={entity}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={selected.has(entity)}
                            onCheckedChange={() => toggle(entity)}
                          />
                          <span className="text-sm">
                            {ENTITY_META[entity].label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

              <Separator />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(new Set(SUPPORTED_ENTITIES))}
                >
                  Select all
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                >
                  Clear all
                </Button>
                <span className="ml-auto self-center text-xs text-muted-foreground">
                  {selected.size} / {SUPPORTED_ENTITIES.length} selected
                </span>
              </div>
              <Button
                onClick={handleExport}
                disabled={!canExport || isExporting || selected.size === 0}
                className="w-full"
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? "Exporting…" : "Download backup"}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import
              </CardTitle>
              <CardDescription>
                Restore from a JSON bundle. Rows are merged by id (existing
                rows update, new rows insert).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Import overwrites rows with matching ids. Imported users
                    are deactivated and have no password — set one before they
                    can log in.
                  </p>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFilePick(f);
                }}
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={!canImport}
                className="w-full"
              >
                <Database className="mr-2 h-4 w-4" />
                Choose backup file…
              </Button>
              {importResult && <ImportResultSummary result={importResult} />}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={!!pendingBundle}
        onOpenChange={(open) => !open && setPendingBundle(null)}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Restore from backup?</DialogTitle>
            <DialogDescription>
              {pendingBundle && (
                <span>
                  Bundle exported{" "}
                  <span className="font-mono">{pendingBundle.exportedAt}</span>.
                  Contains{" "}
                  {Object.keys(pendingBundle.entities).length} entity types
                  (
                  {Object.values(pendingBundle.entities)
                    .reduce((sum, rows) => sum + (rows?.length ?? 0), 0)}{" "}
                  rows total). Rows with matching ids will be updated in place.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingBundle(null)}
              disabled={importData.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={importData.isPending}
            >
              {importData.isPending ? "Importing…" : "Confirm restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportResultSummary({ result }: { result: ImportResult }) {
  return (
    <div className="rounded-md border bg-muted/40 p-3 text-sm">
      <p className="mb-2 font-medium">
        Imported {result.totalRows} rows across {result.summaries.length}{" "}
        entities
      </p>
      <div className="space-y-1">
        {result.summaries.map((s) => (
          <div key={s.entity} className="flex items-center justify-between text-xs">
            <span className="font-mono">{s.entity}</span>
            <span className="flex items-center gap-2 text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                {s.inserted + s.updated}
              </span>
              {s.failed > 0 && (
                <span
                  className="flex items-center gap-1 text-destructive"
                  title={s.errors.slice(0, 3).join("; ")}
                >
                  <XCircle className="h-3 w-3" />
                  {s.failed}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
